/* eslint-disable import/no-cycle */

/* eslint-disable no-param-reassign */
import { toast } from 'react-toastify';

import axios from 'axios';

import i18n from 'shared/lib/i18n';

import { ADD_ABORT_CONTROLLER } from './actionConstants';

import {
  getIsReadOnlyAdmin,
  isValidBuildHash,
  loadAdminBuildHash,
  loadToken,
  notifyAboutNewVersion,
  saveAdminBuildHash,
} from './utils';

import env from '../env';
import { store } from './store';

const { API_ROOT } = env;
const encode = encodeURIComponent;

let token = loadToken();
let sessionToken = null;

const getTodayCountParam = (queryParams = {}) =>
  queryParams.todayCount ? `today-count/` : ``;
const getFrequencyStatsParam = (queryParams = {}) =>
  queryParams.frequencyStats && queryParams.useFrequencyUrl
    ? `frequency-stats/`
    : ``;

const buildHashPlugin = (headers) => {
  const currentBuildHash = headers?.http_latest_admin_build_hash;
  const prevBuildHash = loadAdminBuildHash();
  if (
    isValidBuildHash(currentBuildHash) &&
    isValidBuildHash(prevBuildHash) &&
    currentBuildHash !== prevBuildHash
  ) {
    notifyAboutNewVersion(currentBuildHash);
  } else {
    currentBuildHash && saveAdminBuildHash(currentBuildHash);
  }
};

const queryParamsPlugin = (queryParams) => {
  if (!queryParams) return {};
  const {
    date_after,
    date_before,
    todayCount,
    frequencyStats,
    useFrequencyUrl,
    ...rest
  } = queryParams;
  if (!date_after && !date_before) return rest;
  if (date_after) {
    rest.date_after = date_after.slice(0, 10);
  }
  if (date_before) {
    rest.date_before = date_before.slice(0, 10);
  }
  return rest;
};

const shouldAddSignal = (config) => {
  const { signal, url, method } = config;

  if (method !== 'get') return false;
  if (signal) return false;

  const allowedInBackground = [
    '/login/',
    '/dashboard/',
    '/me/',
    '/service/',
    '/timezones/',
  ];

  return !allowedInBackground.find((key) => url.includes(key));
};

const useRequestSignal = () => {
  const controller = new AbortController();
  store.dispatch({ type: ADD_ABORT_CONTROLLER, controller });
  return controller?.signal;
};

const responseInterceptor = (response) => {
  const { headers, data } = response;
  buildHashPlugin(headers);
  return data;
};

const requestInterceptor = (config) => {
  if (getIsReadOnlyAdmin() && config.method !== 'get') {
    const controller = new AbortController();
    config.signal = controller?.signal;
    controller.abort();
    toast.warn(i18n.t('user.noPermissionWarning'), {
      toastId: 'read-only-admin',
    });
    return config;
  }
  if (shouldAddSignal(config)) {
    config.signal = useRequestSignal();
  }
  if (token) {
    config.headers.Authorization = `JWT ${token}`;
  }
  if (sessionToken) {
    config.headers['X-Call-Center-Session'] = sessionToken;
  }

  const userLang = localStorage.getItem('i18nextLng');
  if (userLang) {
    config.headers['Preferred-Language'] = userLang;
  }

  return config;
};

const onInterceptorError = (error) => Promise.reject(error);

const BASE_URL = `${API_ROOT}/api/v1.4.11`;

const instance = axios.create({
  baseURL: BASE_URL,
});

instance.interceptors.request.use(requestInterceptor, onInterceptorError);
instance.interceptors.response.use(responseInterceptor, onInterceptorError);

const requests = {
  get: (url, query) => {
    const { controller, ...queryParams } = query || {};
    const todayCount = getTodayCountParam(queryParams);
    const frequencyStats = getFrequencyStatsParam(queryParams);
    const params = queryParamsPlugin(queryParams);
    url = `${url}${todayCount}${frequencyStats}`;

    return instance.get(url, {
      params,
      signal: controller?.signal,
    });
  },

  put: (url, body) => instance.put(url, body),
  post: (url, body, config) => instance.post(url, body, config),
  patch: (url, body) => instance.patch(url, body),
  delete: (url, body) => instance.delete(url, body),
};

// is not used
const accessPoints = {
  all: (queryParams = {}) =>
    requests.get('/admin/properties/buildings/access-points/', queryParams),
};

const accessPoint = {
  openDoor: (door) => requests.post(`/doors/${door.id}/open/`),
  openAccessPoint: (accessPoint) =>
    requests.post(`/access-points/${accessPoint.id}/open/`),
  openAccessPointForVendor: (accessPointId, data) =>
    requests.post(`/access-points/${accessPointId}/vendor-open/`, data),
  openAccessPointForDelivery: (accessPoint, deliveryId) =>
    requests.post(
      `/access-points/${accessPoint.id}/open/delivery/${deliveryId}/`,
    ),
  openAccessPointForVisitor: (accessPoint, visitor) =>
    requests.post(
      `/access-points/${accessPoint.id}/open/visitor/${visitor.id}/`,
    ),
  openAccessPointForPerson: (accessPoint, person) =>
    requests.post(`/access-points/${accessPoint.id}/open/person/${person.id}/`),
  openAccessPointForEmergency: (accessPoint, data) =>
    requests.post(`/access-points/${accessPoint.id}/emergency-open/`, data),
  update: (buildingId, doorId, data) =>
    requests.put(
      `/admin/properties/buildings/${buildingId}/access-points/${doorId}/`,
      data,
    ),
  deviceModels: () => requests.get(`/admin/access-point-models/`),
};

const admin = {
  all: (queryParams = {}) => requests.get('/admin/admins/', queryParams),
  allPersons: (queryParams = {}) =>
    requests.get('/admin/persons/', queryParams),
  globalPersonSearch: (queryParams = {}) =>
    requests.get('/admin/persons/global-search/', queryParams),
  getPersonById: (personId) => requests.get(`/admin/persons/${personId}/`),
  create: (data) => requests.post('/admin/admins/', data),
  update: (personId, data) => requests.put(`/admin/admins/${personId}/`, data),
  remove: (personId) => requests.delete(`/admin/admins/${personId}/`, {}),
  resendInvite: (personId) =>
    requests.post(`/admin/admins/${personId}/invite/`),
  wipePersonData: (personId) =>
    requests.post(`/persons/${personId}/remove-pii/`),
};

const auth = {
  login: (username, password) =>
    requests.post('/admin/login/', { username, password }),
  socialLogin: (backend, data) =>
    requests.post(`/admin/login/${backend}/`, data),
  current: () => requests.get('/admin/me/'),
  updateCurrent: (data) => requests.put('/admin/me/', data),
  setPassword: (data) => requests.post('/admin/admins/complete/', data),
  forgotPassword: (username) =>
    requests.post('/admin/password/reset/', { username }),
  confirmPassword: (data) =>
    requests.post('/auth/password/complete-reset/', data),
  signup: (data) => requests.post('/auth/register/', data),
  socialSignup: (backend, data) =>
    requests.post(`/auth/register/${backend}/`, data),
  getRegisteredUser: () => requests.get('/me/'),
  verify: (data) => requests.post('/auth/verify/', data),
  exchangeToken: (data) => instance.post(`/admin/token/exchange/`, data),
};

const aux = {
  getCountries: (queryParams) => requests.get('/countries/', queryParams),
  getStates: (id) => requests.get(`/countries/${id}/states/`),
  getService: () => requests.get('/service/'),
  getTimezones: () => requests.get(`/timezones/`),
};

const building = {
  all: (queryParams) =>
    requests.get('/admin/properties/buildings/new-list/', queryParams),
  allWithoutPagination: () =>
    requests.get('/admin/properties/buildings/new-list/', {
      disable_pagination: true,
    }),
  getBuildingsForFilter: (queryParams) =>
    requests.get('/admin/properties/buildings/light-list/', queryParams),

  create: (data) => requests.post('/admin/properties/buildings/', data),
  update: (buildingId, data) =>
    requests.put(`/admin/properties/buildings/${encode(buildingId)}/`, data),
  get: (buildingId) =>
    requests.get(`/admin/properties/buildings/${encode(buildingId)}/`),

  residentsWithoutPagination: (buildingId) =>
    requests.get(`/admin/properties/buildings/${buildingId}/residents/`, {
      disable_pagination: true,
    }),
  recipientsWithoutPagination: (buildingId) =>
    requests.get(`/admin/properties/buildings/${buildingId}/recipients/`, {
      disable_pagination: true,
    }),
  residentsLightList: (buildingId) =>
    requests.get(
      `/admin/properties/buildings/${buildingId}/residents/light-list/`,
      {
        disable_pagination: true,
      },
    ),
  residents: (buildingId, queryParams, type) =>
    requests.get(
      `/admin/properties/buildings/${buildingId}/${type}/`,
      queryParams,
    ),
  residentsDetailed: (buildingId, queryParams, type) =>
    requests.get(
      `/admin/properties/buildings/${buildingId}/${type}/detailed-list/`,
      queryParams,
    ),
  residentById: (buildingId, residentId) =>
    requests.get(
      `/admin/properties/buildings/${buildingId}/residents/${residentId}/`,
    ),
  resendInviteToAllResidentsInfo: (buildingId) =>
    requests.get(
      `/admin/properties/buildings/${buildingId}/residents/bulk-invite/`,
    ),
  resendInviteToAllResidents: (buildingId) =>
    requests.post(
      `/admin/properties/buildings/${buildingId}/residents/bulk-invite/`,
    ),

  staffWithoutPagination: (buildingId) =>
    requests.get(`/admin/properties/buildings/${buildingId}/staff/`, {
      disable_pagination: true,
    }),
  staffLightList: (buildingId) =>
    requests.get(
      `/admin/properties/buildings/${buildingId}/staff/light-list/`,
      {
        disable_pagination: true,
      },
    ),
  staff: (buildingId, queryParams) =>
    requests.get(
      `/admin/properties/buildings/${buildingId}/staff/`,
      queryParams,
    ),

  staffById: (buildingId, staffId) =>
    requests.get(`/admin/properties/buildings/${buildingId}/staff/${staffId}/`),

  openDoor: (buildingId) =>
    requests.post(`/properties/buildings/${buildingId}/open-door/`),
  openDoorById: (buildingId, doorId) =>
    requests.post(`/properties/buildings/${buildingId}/door/${doorId}/open/`),
  delete: (buildingId) =>
    requests.delete(`/admin/properties/buildings/${buildingId}/`),

  getDoors: (queryParams, id) =>
    requests.get(`/admin/properties/buildings/${id}/doors/`, queryParams),
  getDoor: (id, doorId) =>
    requests.get(`/admin/properties/buildings/${id}/doors/${doorId}/`),
  createDoor: (buildingId, data) =>
    requests.post(`/admin/properties/buildings/${buildingId}/doors/`, data),
  updateDoor: (buildingId, doorId, data) =>
    requests.put(
      `/admin/properties/buildings/${buildingId}/doors/${doorId}/`,
      data,
    ),
  deleteDoor: (buildingId, doorId) =>
    requests.delete(
      `/admin/properties/buildings/${buildingId}/doors/${doorId}/`,
    ),
  createDoorSchedule: (buildingId, doorId, schedule) =>
    requests.post(
      `/admin/properties/buildings/${buildingId}/doors/${doorId}/schedule/`,
      schedule,
    ),
  setDoorsOrder: (buildingId, data) =>
    requests.put(
      `/admin/properties/buildings/${buildingId}/doors/order/`,
      data,
    ),

  getAccessPoints: (id, queryParams) =>
    requests.get(
      `/admin/properties/buildings/${id}/access-points/`,
      queryParams,
    ),
  createAccessPoint: (id, data) =>
    requests.post(`/admin/properties/buildings/${id}/access-points/`, data),
  updateAccessPoint: (id, accessPointId, data) =>
    requests.put(
      `/admin/properties/buildings/${id}/access-points/${accessPointId}/`,
      data,
    ),
  getCameras: (buildingId, queryParams) =>
    requests.get(
      `/admin/properties/buildings/${buildingId}/cameras/`,
      queryParams,
    ),
  createCamera: (buildingId, data) =>
    requests.post(`/admin/properties/buildings/${buildingId}/cameras/`, data),
  updateCamera: (buildingId, cameraId, data) =>
    requests.put(
      `/admin/properties/buildings/${buildingId}/cameras/${cameraId}/`,
      data,
    ),
  deleteCamera: (buildingId, cameraId) =>
    requests.delete(
      `/admin/properties/buildings/${buildingId}/cameras/${cameraId}/`,
    ),
  setCamerasOrder: (buildingId, data) =>
    requests.put(
      `/admin/properties/buildings/${buildingId}/cameras/order/`,
      data,
    ),
  getServiceRequestCategories: (buildingId) =>
    requests.get(
      `/properties/buildings/${buildingId}/service-request-categories/`,
    ),
  yardi: (buildingId) =>
    requests.get(
      `/admin/properties/buildings/${buildingId}/yardi-sync-report/`,
      {},
    ),
  yardiResync: (buildingId, data) =>
    requests.post(
      `/admin/properties/buildings/${buildingId}/sync/yardi/`,
      data,
    ),
  yardiSRAndResidentsSync: (buildingId, data) =>
    requests.post(
      `/admin/properties/buildings/${buildingId}/service-requests/sync/yardi/`,
      data,
    ),
  yardiSRCategoriesSync: (buildingId, data) =>
    requests.post(
      `/admin/properties/buildings/${buildingId}/auto-sync/yardi/service-requests-categories/`,
      data,
    ),
  yardiStatuses: (buildingId, queryParams) =>
    requests.get(
      `/admin/properties/buildings/${buildingId}/yardi-service-requests-statuses-data/`,
      queryParams,
    ),
  syncPersonWithBrivo: (buildingId, personId) =>
    requests.post(
      `/properties/buildings/${buildingId}/persons/${personId}/sync/brivo/`,
    ),
  syncPersonWithBMX: (buildingId, personId) =>
    requests.post(
      `/properties/buildings/${buildingId}/persons/${personId}/sync/bmx/`,
    ),
  eagleEyeSession: (buildingId, queryParams = {}) =>
    requests.get(
      `/properties/buildings/${buildingId}/eagleeye/session/`,
      queryParams,
    ),
  getIntegrationsStatus: (buildingId) =>
    requests.get(
      `/admin/properties/buildings/${buildingId}/integrations-status/`,
    ),
  putIntegrationsStatus: (buildingId, data) =>
    requests.put(
      `/admin/properties/buildings/${buildingId}/integrations-status/`,
      data,
    ),
  getDoorsOpeningLog: (buildingId, queryParams = {}) =>
    requests.get(
      `/admin/properties/buildings/${buildingId}/doors/log/`,
      queryParams,
    ),
  getBuildingSettings: (buildingId) =>
    requests.get(`/admin/properties/buildings/${buildingId}/settings/`),
  putBuildingSettings: (buildingId, data) =>
    requests.put(`/admin/properties/buildings/${buildingId}/settings/`, data),
  getBMXAccessToken: (buildingId) =>
    requests.get(`/properties/buildings/${buildingId}/access-token/bmx/`),
  syncBMXDoors: (buildingId) =>
    requests.post(`/properties/buildings/${buildingId}/devices/sync/bmx/`),
  syncBMXPersons: (buildingId) =>
    requests.post(`/properties/buildings/${buildingId}/persons/sync/bmx/`),
  syncYardiResidents: (buildingId, data) =>
    requests.post(
      `/admin/properties/buildings/${buildingId}/auto-sync/yardi/`,
      data,
    ),
  sendBMXCameraStatus: (buildingId, cameraId, data) =>
    requests.put(
      `/admin/properties/buildings/${buildingId}/cameras/${cameraId}/status/`,
      data,
    ),
  getDeliverySnapshotData: (buildingId) =>
    requests.get(`/admin/properties/buildings/${buildingId}/deliveries-data/`),
  putDeliverySnapshotData: (buildingId, data) =>
    requests.put(
      `/admin/properties/buildings/${buildingId}/deliveries-data/`,
      data,
    ),
  getYardiUnits: (buildingId, queryParams) =>
    requests.get(
      `/admin/properties/buildings/${buildingId}/units/yardi-data/`,
      queryParams,
    ),
  syncPersonWithSmartAir: (buildingId, personId) =>
    requests.post(
      `/properties/buildings/${buildingId}/persons/sync/smartair/`,
      { persons: [personId] },
    ),
};

const buildingSettings = {
  getPropertySettings: (buildingId) =>
    requests.get(`/admin/properties/buildings/${buildingId}/settings-new/`),
  putPropertySettings: (buildingId, data) =>
    requests.put(
      `/admin/properties/buildings/${buildingId}/settings-new/`,
      data,
    ),
  getLeasingSettings: (buildingId) =>
    requests.get(`/admin/properties/buildings/${buildingId}/leasing-settings/`),
  putLeasingSettings: (buildingId, data) =>
    requests.put(
      `/admin/properties/buildings/${buildingId}/leasing-settings/`,
      data,
    ),
  getRemoteDoormanSettings: (buildingId) =>
    requests.get(
      `/admin/properties/buildings/${buildingId}/remote-doorman-settings/`,
    ),
  putRemoteDoormanSettings: (buildingId, data) =>
    requests.put(
      `/admin/properties/buildings/${buildingId}/remote-doorman-settings/`,
      data,
    ),
};

const buildingOperations = {
  getInfo: (buildingId) =>
    requests.get(`/admin/properties/buildings/${buildingId}/info/`),
  updateInfo: (buildingId, data) =>
    requests.put(`/admin/properties/buildings/${buildingId}/info/`, data),
  uploadPicture: (buildingId, data) =>
    requests.post(`/admin/properties/buildings/${buildingId}/picture/`, data),
  deletePicture: (buildingId, pictureId) =>
    requests.delete(
      `/admin/properties/buildings/${buildingId}/picture/${pictureId}/`,
    ),
};

const deliveryTypes = {
  getDeliveryTypes: (queryParams) =>
    requests.get(`/deliveries/types/`, queryParams),
  getBuildingDeliveryTypes: (buildingId, queryParams) =>
    requests.get(`/properties/${buildingId}/deliveries/types/`, queryParams),
  putPropertyDeliveryTypes: (propertyId, data) =>
    requests.put(`/properties/${propertyId}/deliveries/types/`, data),
  getDeliveryTypeById: (id) => requests.get(`/deliveries/types/${id}/`),
  createDeliveryType: (data) => requests.post(`/deliveries/types/`, data),
  updateDeliveryType: (id, data) =>
    requests.put(`/deliveries/types/${id}/`, data),
  removeDeliveryType: (id) => requests.delete(`/deliveries/types/${id}/`),
};

const sync = {
  devices: (buildingId, type) =>
    requests.post(`/properties/buildings/${buildingId}/devices/sync/${type}/`),
  persons: (buildingId, type) =>
    requests.post(`/properties/buildings/${buildingId}/persons/sync/${type}/`),
  person: (buildingId, personId, type) =>
    requests.post(
      `/properties/buildings/${buildingId}/persons/${personId}/sync/${type}/`,
    ),
  dormakaba: (buildingId, data) =>
    requests.post(
      `/properties/buildings/${buildingId}/devices/sync/dormakaba/`,
      data,
    ),
};

const visitorKeysData = {
  get: (id) =>
    requests.get(`/admin/properties/buildings/${id}/visitor-keys-data/`),
  put: (data) =>
    requests.put(`/admin/properties/buildings/visitor-keys-data/`, data),
  getAll: () => requests.get(`/admin/properties/buildings/all/`),
};

const callCenter = {
  get: (phoneNumber) =>
    requests.get(`/admin/dashboard-new/?phone_number=${phoneNumber}`),
  loadResidents: (phoneNumber) =>
    requests.get(`/admin/dashboard/residents?phone_number=${phoneNumber}`),
  loadStaff: (phoneNumber) =>
    requests.get(`/admin/dashboard/staff?phone_number=${phoneNumber}`),
  loadUnits: (phoneNumber) =>
    requests.get(`/admin/dashboard/units?phone_number=${phoneNumber}`),
  loadVisitors: (phoneNumber) =>
    requests.get(`/admin/dashboard/visitors?phone_number=${phoneNumber}`),
  createDelivery: (data) => requests.post('/deliveries/', data),
  createGeneralDelivery: (data) => requests.post('/deliveries/general/', data),
  createVisitor: (data) => requests.post('/visitors/', data),
  rejectVisitor: (buildingId, data) =>
    requests.post(
      `/admin/properties/buildings/${buildingId}/reject-visitor/`,
      data,
    ),
  submitLeasingMessage: (buildingId, data) =>
    requests.post(
      `/properties/buildings/${buildingId}/leasing-office-message/`,
      data,
    ),
  noAction: (buildingId) =>
    requests.post(`/admin/properties/buildings/${buildingId}/no-actions/`),
  agentsList: (queryParams) =>
    requests.get(`/admin/call-center-agents/`, queryParams),
  agentsLog: (queryParams) =>
    requests.get('/admin/call-center-agents/log/', queryParams),
  createCCAgent: (data) => requests.post(`/admin/call-center-agents/`, data),
  updateCCAgent: (agentId, data) =>
    requests.put(`/admin/call-center-agents/${agentId}/`, data),
  deleteCCAgent: (agentId) =>
    requests.delete(`/admin/call-center-agents/${agentId}/`),
};

const contacts = {
  confirm: (data) => requests.post('/admin/contacts/confirm/', data),
  confirmById: (id, data) =>
    requests.post(`/admin/contacts/${id}/confirm/`, data),
};

const dashboard = {
  get: (queryParams) => requests.get('/dashboard/', queryParams || {}),
  getCourierErrors: (propertyId, queryParams) =>
    requests.get(
      `/admin/dashboard/property/${propertyId}/courier-errors/`,
      queryParams,
    ),
  courierError: (data) =>
    requests.post(`/admin/dashboard/courier-error/`, data),
};

const deliveries = {
  get: (queryParams) => requests.get('/deliveries/all/', queryParams || {}),
  update: (deliveryId, data) =>
    requests.put(`/deliveries/${deliveryId}/`, data),
  signOut: (data) => requests.put(`/deliveries/sign-out/`, data),
  getQrCode: (buildingId) =>
    requests.post(
      `/admin/properties/buildings/${buildingId}/front-desk/qr/`,
      {},
      { responseType: 'blob' },
    ),
};

const person = {
  allInBuilding: (buildingId) =>
    requests.get(`/admin/persons/?building=${encode(buildingId)}`), // TODO: not used?
  resendInvite: (personId) => requests.post(`/persons/${personId}/invite/`),
  manageCredentials: (personId, data) =>
    requests.put(`/persons/${personId}/credentials/`, data),
  allowMultipleDevices: (personId, data) =>
    requests.put(`/admin/persons/${personId}/allow-multiple-devices/`, data),
};

const image = {
  upload: (url, fileData) => requests.post(url, fileData),
};

const pmc = {
  all: (queryParams) => requests.get('/admin/properties/pmcs/', queryParams),
  allReducedData: () => requests.get('/admin/properties/pmcs/all/'),

  get: (pmcId) => requests.get(`/admin/properties/pmcs/${encode(pmcId)}/`),
  buildingsWithoutPagination: (pmcId) =>
    requests.get(`/admin/properties/pmcs/${pmcId}/buildings/new-list/`, {
      disable_pagination: true,
    }),
  staff: (pmcId, queryParams) =>
    requests.get(`/admin/properties/pmcs/${pmcId}/staff/`, queryParams),
  staffById: (pmcId, staffId) =>
    requests.get(`/admin/properties/pmcs/${pmcId}/staff/${staffId}/`),
  staffWithoutPagination: (pmcId) =>
    requests.get(`/admin/properties/pmcs/${pmcId}/staff/`, {
      disable_pagination: true,
    }),
  create: (data) => requests.post('/admin/properties/pmcs/', data),
  update: (pmcId, data) =>
    requests.put(`/admin/properties/pmcs/${pmcId}/`, data),
  delete: (pmcId) => requests.delete(`/admin/properties/pmcs/${pmcId}/`),
  cameras: (pmcId, queryParams) =>
    requests.get(
      `/admin/properties/pmcs/${encode(pmcId)}/cameras/`,
      queryParams,
    ),
  getDoors: (pmcId, queryParams) =>
    requests.get(`/admin/properties/pmcs/${pmcId}/doors/`, queryParams),
};

const ip = {
  all: (queryParams) => requests.get('/admin/ip-whitelist/', queryParams),
  create: (data) => requests.post('/admin/ip-whitelist/', data),
  reload: (data) => requests.post(`/admin/ip-whitelist/reload/`, data),
  delete: (ipId) => requests.delete(`/admin/ip-whitelist/${ipId}/`),
};

const resident = {
  create: (data) => requests.post('/residents/', data),
  allInBuilding: (buildingId) =>
    requests.get(`/residents/?building=${buildingId}`),
  setSkipMoveIn: (personId, data) =>
    requests.put(`/admin/persons/${personId}/skip-move-in/`, data),
  update: (personId, data) => requests.put(`/residents/${personId}/`, data),
  addRole: (data) => requests.post('/residents/complete/', data),
  updateRoleInfo: (personId, data) =>
    requests.put(`/admin/residents/${personId}/person-role/`, data),
  moveOut: (personId, data) =>
    requests.post(`/residents/${personId}/move-out/`, data),
  moveOutBoardMember: (personId, data) =>
    requests.delete(
      `/admin/buildings/${data.buildingId}/residents/${personId}/board-member/`,
    ),
  isRegisteredWithBMX: (personId) =>
    requests.get(`/bmx/persons/${personId}/registration_status/`, {}),
  resendBMXInvite: (personId) =>
    requests.post(`/bmx/persons/${personId}/resend_invite/`, {}),
  activity: (buildingId, personId, queryParams) =>
    requests.get(
      `/admin/properties/buildings/${buildingId}/residents/${personId}/activity/`,
      queryParams,
    ),
  activityOverview: (buildingId, personId) =>
    requests.get(
      `/admin/properties/buildings/${buildingId}/residents/${personId}/actions/`,
    ),
  getNotifications: (buildingId, personId, queryParams) =>
    requests.get(
      `/admin/properties/buildings/${buildingId}/residents/${personId}/notifications/`,
      queryParams,
    ),
};

const serviceRequest = {
  get: (queryParams) => requests.get('/admin/service-requests/', queryParams),
  getById: (serviceRequestId) =>
    requests.get(`/service-requests/${serviceRequestId}/`),
  changeStatusOrComment: (serviceRequestId, data) =>
    requests.post(`/service-requests/${serviceRequestId}/comments/`, data),
  getComments: (serviceRequestId, queryParams) =>
    requests.get(
      `/service-requests/${serviceRequestId}/comments/`,
      queryParams,
    ),
  create: (data) => requests.post('/service-requests/', data),
  markAsRead: (data) => requests.put('/service-requests/', data),
  update: (serviceRequestId, data) =>
    requests.put(`/service-requests/${serviceRequestId}/`, data),
  getSRCategories: (id) =>
    requests.get(`/admin/properties/${id}/service-request-categories/`),
  postSRCategories: (data) =>
    requests.post(`/admin/properties/service-request-categories/`, data),
  deleteSRCategory: (categoryId) =>
    requests.delete(
      `/admin/properties/service-request-categories/${categoryId}/`,
    ),
  updateSRCategories: (data, categoryId) =>
    requests.put(
      `/admin/properties/service-request-categories/${categoryId}/`,
      data,
    ),
  getServiceRequestsCounts: (queryParams) =>
    requests.get('/service-requests/counts/', queryParams),
  getServiceRequestsIds: (queryParams) =>
    requests.get(
      '/admin/service-requests/carson-service-requests-ids/',
      queryParams,
    ),
  getServiceRequestsPriorities: (buildingId) =>
    requests.get(
      `/properties/buildings/${buildingId}/service-request-priorities/`,
    ),
  getSRStatuses: (buildingId) =>
    requests.get(
      `/properties/buildings/${buildingId}/service-request-yardi-statuses/`,
    ),
};

const staff = {
  create: (data) => requests.post('/staff/', data),
  allInBuilding: () => requests.get('/staff/'),
  update: (personId, data) => requests.put(`/staff/${personId}/`, data),
  addRole: (data) => requests.post('/staff/complete/', data),
  moveOut: (personId, data) =>
    requests.post(`/staff/${personId}/move-out/`, data),
  updateRole: (personRoleId, data) =>
    requests.put(`/admin/staff/person-roles/${personRoleId}/`, data),
};

const unit = {
  all: (queryParams, buildingId) =>
    requests.get(
      `/admin/properties/buildings/${buildingId}/units/`,
      queryParams,
    ),
  getResidents: (unitId) =>
    requests.get(`/properties/units/${unitId}/residents/`, {
      disable_pagination: true,
    }),
  create: (buildingId, data) =>
    requests.post(`/admin/properties/buildings/${buildingId}/units/`, data),
  update: (buildingId, unitId, data) =>
    requests.put(
      `/admin/properties/buildings/${buildingId}/units/${unitId}/`,
      data,
    ),
  setOrder: (buildingId, data) =>
    requests.put(
      `/admin/properties/buildings/${buildingId}/units/order/`,
      data,
    ),
  delete: (buildingId, unitId) =>
    requests.delete(
      `/admin/properties/buildings/${buildingId}/units/${unitId}/`,
    ),
  getUnitsWithContacts: (buildingId) =>
    requests.get(`/properties/buildings/${buildingId}/units-with-contacts/`),
  activityOverview: (unitId) =>
    requests.get(`/admin/properties/units/${unitId}/activity/`),
  searchUnits: (buildingId, queryParams) =>
    requests.get(
      `/admin/properties/buildings/${buildingId}/front-desk/`,
      queryParams,
    ),
  getUnitById: (buildingId, unitId) =>
    requests.get(`/admin/properties/buildings/${buildingId}/units/${unitId}/`),
};

const visitors = {
  getLog: (queryParams) => requests.get('/admin/visitor-log/', queryParams),
  get: (queryParams) => requests.get('/admin/visitors/', queryParams),

  createVisitor: (data) => requests.post('/admin/visitors/', data),
  completeVisitorCreation: (data) =>
    requests.post('/admin/visitors/complete/', data),
  createBroker: (data) => requests.post('/visitors/brokers/', data),
  completeBrokerCreation: (data) =>
    requests.post('/visitors/brokers/complete/', data),
  updateVisitor: (data, personId) =>
    requests.put(`/admin/visitors/${personId}/`, data),
  completeVisitorUpdate: (data, personId) =>
    requests.put(`/admin/visitors/${personId}/complete/`, data),

  getById: (visitorId) => requests.get(`/admin/visitors/${visitorId}/`),
  expireVisitor: (visitorId) =>
    requests.post(`/admin/visitors/${visitorId}/expire/`),
  getInviteInfo: (visitorId) =>
    requests.get(`/admin/visitors/${visitorId}/invite/`),
  getVisitorLog: (visitorId) =>
    requests.get(`/admin/visitors/${visitorId}/log/`),

  sendInvite: (data, visitorId) =>
    requests.post(`/visitors/${visitorId}/invite/`, data),
  resendInvite: (visitorId) =>
    requests.post(`/visitors/${visitorId}/invite/resend/`),
  updateInvite: (visitorId, data) =>
    requests.put(`/visitors/${visitorId}/invite/`, data),
  deleteInvite: (visitorId) =>
    requests.delete(`/visitors/${visitorId}/invite/`),

  logEntry: (visitorId) => requests.post(`/visitors/${visitorId}/entry/`),
};

const messages = {
  getThreads: (queryParams) => requests.get('/threads/', queryParams),
  getThreadsScopes: () => requests.get('/threads/scopes/'),
  getChatById: (id, queryParams) =>
    requests.get(`/threads/${id}/`, queryParams),
  markChatAsRead: (id) => requests.post(`/threads/${id}/`),
  putNewMessage: (threadId, data) =>
    requests.put(`/threads/${threadId}/`, data),
  getRecipients: (queryParams) =>
    requests.get(`/threads/recipients/`, queryParams),
  createThread: (data) => requests.post(`/threads/`, data),
  getNumberOfRecipients: (data) =>
    requests.post(`/threads/recipients-count/`, data),
  getRoles: () => requests.get(`/roles/`),

  preuploadDocument: (data) =>
    requests.post(`/threads/documents/upload/`, data),
  checkDocumentNewThread: (data) =>
    requests.post(`/threads/documents/check/`, data),
  checkDocumentExistingThread: (data, threadId) =>
    requests.post(`/threads/${threadId}/documents/check/`, data),
};

const markAsViewed = {
  byScopes: (data) => requests.post('/mark-as-viewed/', data),
};

const notifications = {
  getNotificationsList: (queryParams) =>
    requests.get('/notifications/', queryParams),
  markAsViewedByIds: (data) => requests.put('/notifications/', data),
};

const notificationPreferences = {
  getNotificationPreferences: () => requests.get('/notification-preferences/'),
  putNotificationPreferences: (data) =>
    requests.put('/notification-preferences/', data),
};

// const yardi = {
//   resync: (data) => requests.post('/yardi/sync/', data),
// };

const documentLibrary = {
  getDirectories: (queryParams) => requests.get('/directories/', queryParams),
  deleteDirectories: (dirID) => requests.delete(`/directories/${dirID}/`),
  getDirectoryData: (dirID) => requests.get(`/directories/${dirID}/`),
  createDirectory: (data, dirID) =>
    requests.post(`/directories/${dirID}/`, data),
  updateDirectory: (data, dirID) =>
    requests.put(`/directories/${dirID}/`, data),
  getNumberOfDirectories: (dirID) =>
    requests.get(`/directories/${dirID}/children-count/`),
  uploadDocument: (dirID, data) =>
    requests.post(`/directories/${dirID}/documents/`, data),
  checkDocument: (dirID, data) =>
    requests.get(`/directories/${dirID}/documents/check/`, data),
  getDirsAndDocs: (dirID, queryParams) =>
    requests.get(`/directories/${dirID}/objects/`, queryParams),
  searchInFolder: (dirID, queryParams) =>
    requests.get(`/directories/${dirID}/search/`, queryParams),
  globalSearch: (queryParams) =>
    requests.get(`/directories/search/`, queryParams),
  getDocument: (docID) => requests.get(`/documents/${docID}/`),
  deleteDocuments: (docID) => requests.delete(`/documents/${docID}/`),
  getAdminRootDirectories: () =>
    requests.get('/admin/properties/buildings/directories/root-directories/'),
};

const addresses = {
  validateAddress: (data) => requests.post('/addresses/validate/', data),
};

const carsonActivity = {
  getActionsLog: (queryParams) =>
    requests.get(`/admin/actions-log/`, queryParams),
  getActionsLogNames: () => requests.get(`/admin/actions-log-names/`),
  getPersonActionsLog: (personId, queryParams) =>
    requests.get(`/admin/persons/${personId}/actions-log/`, queryParams),
};

const systemExternalAccounts = {
  getSchema: () => requests.get(`/admin/accounts/scheme/`),
  getByType: (accountType, queryParams) =>
    requests.get(`/admin/accounts/${accountType}/`, queryParams),
  getById: (accountType, accountId) =>
    requests.get(`/admin/accounts/${accountType}/${accountId}/`),
  createByType: (accountType, data) =>
    requests.post(`/admin/accounts/${accountType}/`, data),
  updateById: (accountType, accountId, data) =>
    requests.put(`/admin/accounts/${accountType}/${accountId}/`, data),
  deleteById: (accountType, accountId) =>
    requests.delete(`/admin/accounts/${accountType}/${accountId}/`),
};

const buildingExternalAccounts = {
  getSchema: (buildingId) =>
    requests.get(`/admin/properties/buildings/${buildingId}/accounts/scheme/`),
  getAccountsByType: (buildingId, accountType) =>
    requests.get(
      `/admin/properties/buildings/${buildingId}/accounts/${accountType}/`,
    ),
  getSubtypeSchema: (buildingId, accountType, accountSubtype) =>
    requests.get(
      `/admin/properties/buildings/${buildingId}/accounts/${accountType}/${accountSubtype}/scheme/`,
    ),
  getById: (buildingId, accountType, accountSubtype, accountId) =>
    requests.get(
      `/admin/properties/buildings/${buildingId}/accounts/${accountType}/${accountSubtype}/${accountId}/`,
    ),
  create: (buildingId, accountType, accountSubtype, data) =>
    requests.post(
      `/admin/properties/buildings/${buildingId}/accounts/${accountType}/${accountSubtype}/`,
      data,
    ),
  update: (buildingId, accountType, accountSubtype, accountId, data) =>
    requests.put(
      `/admin/properties/buildings/${buildingId}/accounts/${accountType}/${accountSubtype}/${accountId}/`,
      data,
    ),
  delete: (buildingId, accountType, accountSubtype, accountId) =>
    requests.delete(
      `/admin/properties/buildings/${buildingId}/accounts/${accountType}/${accountSubtype}/${accountId}/`,
    ),
  sync: (buildingId, syncType, accountType) =>
    requests.post(
      `/properties/buildings/${buildingId}/${syncType}/sync/${accountType}/`,
    ),
};

const externalAccount = {
  getAccount: (accountType, accountId) =>
    requests.get(`/admin/external-accounts/${accountType}/${accountId}/`),
  createAccount: (data, accountType) =>
    requests.post(`/admin/external-accounts/${accountType}/`, data),
  updateAccount: (accountType, accountId, data) =>
    requests.put(`/admin/external-accounts/${accountType}/${accountId}/`, data),
  deleteAccount: (accountType, accountId) =>
    requests.delete(`/admin/external-accounts/${accountType}/${accountId}/`),
};

const paymentsAccounts = {
  getListOfExternalAccounts: (queryParams) =>
    requests.get(
      `/admin/external-accounts/external-payments-accounts/`,
      queryParams,
    ),
  getListOfAptexxAccounts: (queryParams) =>
    requests.get(`/admin/external-accounts/aptexx/`, queryParams),
  getListOfClickpayAccounts: (queryParams) =>
    requests.get(`/admin/external-accounts/clickpay/`, queryParams),
  getAllBuildingPaymentsAccounts: (buildingId) =>
    requests.get(
      `/admin/properties/buildings/${buildingId}/external-accounts/payments/`,
    ),
  getBuildingPaymentsAccount: (buildingId) =>
    requests.get(`/admin/properties/buildings/${buildingId}/payments-account/`),
  assignPaymentsAccountToBuilding: (buildingId, data) =>
    requests.post(
      `/admin/properties/buildings/${buildingId}/payments-account/`,
      data,
    ),
};

const accessControlAccounts = {
  getListByType: (type, queryParams) =>
    requests.get(`/admin/external-accounts/${type}/`, queryParams),

  getAllBuildingACAccounts: (buildingId) =>
    requests.get(
      `/admin/properties/buildings/${buildingId}/external-accounts/access-control/`,
    ),

  getBrivoCleanupUsers: (accountId, queryParams) =>
    requests.get(
      `/admin/external-accounts/brivo/${accountId}/cleanup-users/`,
      queryParams,
    ),
  clearBrivoUsers: (accountId, data) =>
    requests.post(
      `/admin/external-accounts/brivo/${accountId}/cleanup-users/`,
      data,
    ),

  getBuildingBrivoAccount: (buildingId) =>
    requests.get(`/admin/properties/buildings/${buildingId}/brivo-account/`),
  getBuildingSmartairAccount: (buildingId) =>
    requests.get(`/admin/properties/buildings/${buildingId}/smartair-account/`),
  getBuildingComelitAccount: (buildingId) =>
    requests.get(`/admin/properties/buildings/${buildingId}/comelit-account/`),
  getBuilding2NAccount: (buildingId) =>
    requests.get(`/admin/properties/buildings/${buildingId}/2n-account/`),
  getBuildingAkuvoxAccount: (buildingId) =>
    requests.get(`/admin/properties/buildings/${buildingId}/akuvox/`),
  getBuildingBMXAccount: (buildingId) =>
    requests.get(`/admin/properties/buildings/${buildingId}/bmx-account/`),
  getBuildingDormakabaAccount: (buildingId) =>
    requests.get(
      `/admin/properties/buildings/${buildingId}/dormakaba-account/`,
    ),
  getBuildingLyazonAccount: (buildingId) =>
    requests.get(`/admin/properties/buildings/${buildingId}/lyazon-account/`),
  getBuildingZervAccount: (buildingId) =>
    requests.get(`/admin/properties/buildings/${buildingId}/zerv-account/`),
  getBuildingSaltoAccount: (buildingId) =>
    requests.get(`/admin/properties/buildings/${buildingId}/salto-account/`),
  getBuildingBrivoSiteId: (buildingId, data) =>
    requests.post(
      `/admin/properties/buildings/${buildingId}/brivo-account/search/`,
      data,
    ),
  getBuildingKeyscanAccount: (buildingId) =>
    requests.get(`/admin/properties/buildings/${buildingId}/keyscan-account/`),

  associateBrivoWithBuilding: (buildingId, data) =>
    requests.post(
      `/admin/properties/buildings/${buildingId}/brivo-account/`,
      data,
    ),
  associateSmartairWithBuilding: (buildingId, data) =>
    requests.post(
      `/admin/properties/buildings/${buildingId}/smartair-account/`,
      data,
    ),
  associateComelitWithBuilding: (buildingId, data) =>
    requests.post(
      `/admin/properties/buildings/${buildingId}/comelit-account/`,
      data,
    ),
  associate2NWithBuilding: (buildingId, data) =>
    requests.post(
      `/admin/properties/buildings/${buildingId}/2n-accounts/`,
      data,
    ),
  // TODO: use it for all accounts
  associateAccountWithBuilding: (buildingId, account, data) =>
    requests.post(
      `/admin/properties/buildings/${buildingId}/${account}/`,
      data,
    ),
  associateBMXWithBuilding: (buildingId, data) =>
    requests.post(
      `/admin/properties/buildings/${buildingId}/bmx-account/`,
      data,
    ),
  associateDormakabaWithBuilding: (buildingId, data) =>
    requests.post(
      `/admin/properties/buildings/${buildingId}/dormakaba-account/`,
      data,
    ),
  associateLyazonWithBuilding: (buildingId, data) =>
    requests.post(
      `/admin/properties/buildings/${buildingId}/lyazon-account/`,
      data,
    ),
  associateZervWithBuilding: (buildingId, data) =>
    requests.post(
      `/admin/properties/buildings/${buildingId}/zerv-account/`,
      data,
    ),
  associateSaltoWithBuilding: (buildingId, data) =>
    requests.post(
      `/admin/properties/buildings/${buildingId}/salto-account/`,
      data,
    ),
  associateKeyscanWithBuilding: (buildingId, data) =>
    requests.post(
      `/admin/properties/buildings/${buildingId}/keyscan-account/`,
      data,
    ),
  removeTwoNAccount: (buildingId, accountId) =>
    requests.delete(
      `/admin/properties/buildings/${buildingId}/2n-accounts/${accountId}/delete/`,
    ),
  removeComelitAccount: (buildingId, accountId) =>
    requests.delete(
      `/admin/properties/buildings/${buildingId}/comelit-account/${accountId}/delete/`,
    ),
};

const camerasAccounts = {
  getListOfEEAccounts: (queryParams) =>
    requests.get(`/admin/external-accounts/eagleeye/`, queryParams),
  getAllBuildingCamerasAccounts: (buildingId) =>
    requests.get(
      `/admin/properties/buildings/${buildingId}/external-accounts/cameras/`,
    ),
  getBuildingEEAccount: (buildingId) =>
    requests.get(`/admin/properties/buildings/${buildingId}/cameras-account/`),
  assignEEAccountToBuilding: (buildingId, data) =>
    requests.post(
      `/admin/properties/buildings/${buildingId}/cameras-account/`,
      data,
    ),
};

const accountingAccounts = {
  getAllBuildingAccountingAccounts: (buildingId) =>
    requests.get(
      `/admin/properties/buildings/${buildingId}/external-accounts/accounting/`,
    ),

  getBuildingYardiAccount: (buildingId) =>
    requests.get(`/admin/properties/buildings/${buildingId}/yardi-accounts/`),
  getBuildingMriAccount: (buildingId) =>
    requests.get(`/admin/properties/buildings/${buildingId}/mri-accounts/`),
  getBuildingAppfolioAccount: (buildingId) =>
    requests.get(
      `/admin/properties/buildings/${buildingId}/appfolio-accounts/`,
    ),
  getBuildingMDSAccount: (buildingId) =>
    requests.get(`/admin/properties/buildings/${buildingId}/mds-accounts/`),
  assignYardiAccountToBuilding: (buildingId, data) =>
    requests.post(
      `/admin/properties/buildings/${buildingId}/yardi-accounts/`,
      data,
    ),
  assignMriAccountToBuilding: (buildingId, data) =>
    requests.post(
      `/admin/properties/buildings/${buildingId}/mri-accounts/`,
      data,
    ),
  assignAppfolioAccountToBuilding: (buildingId, data) =>
    requests.post(
      `/admin/properties/buildings/${buildingId}/appfolio-accounts/`,
      data,
    ),
  assignMDSAccountToBuilding: (buildingId, data) =>
    requests.post(
      `/admin/properties/buildings/${buildingId}/mds-accounts/`,
      data,
    ),

  updateAccountingAccount: (buildingId, accountId, data, query) =>
    requests.put(
      `/admin/properties/buildings/${buildingId}/external-accounts/accounting/${accountId}/${query}`,
      data,
    ),
  removeAccountingAccount: (buildingId, accountId) =>
    requests.delete(
      `/admin/properties/buildings/${buildingId}/external-accounts/accounting/${accountId}/`,
    ),
};

const customIntegrations = {
  getExternalLinksList: (queryParams) =>
    requests.get(`/external-links/`, queryParams),
  createExternalLink: (data) => requests.post(`/external-links/`, data),
  getExternalLink: (linkId) => requests.get(`/external-links/${linkId}/`),
  updateExternalLink: (linkId, data) =>
    requests.put(`/external-links/${linkId}/`, data),
  deleteExternalLink: (linkId) => requests.delete(`/external-links/${linkId}/`),
  assignExternalLink: (linkId, data) =>
    requests.patch(`/external-links/${linkId}/assign-to-building/`, data),
  unassignExternalLink: (linkId, data) =>
    requests.patch(`/external-links/${linkId}/unassign-from-building/`, data),
  updateBuildingExternalLinks: (buildingId, data) =>
    requests.put(
      `/admin/properties/buildings/${buildingId}/external-links/`,
      data,
    ),
  getBuildingExternalLink: (buildingId) =>
    requests.get(`/admin/properties/buildings/${buildingId}/external-links/`),
};

const registeredUsersCount = {
  buildingStaff: (buildingId, queryParams) =>
    requests.get(`/admin/buildings/${buildingId}/count/staff/`, queryParams),
  buildingResidents: (buildingId, queryParams) =>
    requests.get(
      `/admin/buildings/${buildingId}/count/residents/`,
      queryParams,
    ),
  pmcStaff: (pmcId, queryParams) =>
    requests.get(`/admin/pmc/${pmcId}/count/staff/`, queryParams),
  all: (queryParams) => requests.get(`/admin/count/persons/`, queryParams),
};

const accessCard = {
  getList: (queryParams) =>
    requests.get(`/properties/access-cards/`, queryParams),
  create: (data) => requests.post(`/properties/access-cards/`, data),
  getById: (accessCardId) =>
    requests.get(`/properties/access-cards/${accessCardId}/`),
  deleteById: (accessCardId) =>
    requests.delete(`/properties/access-cards/${accessCardId}/`),
  updateById: (accessCardId, data) =>
    requests.put(`/properties/access-cards/${accessCardId}/`, data),

  getBatchList: (queryParams) =>
    requests.get(`/properties/access-card-batches/`, queryParams),
  createBatch: (data) =>
    requests.post(`/properties/access-card-batches/`, data),
  getBatchById: (accessCardId) =>
    requests.get(`/properties/access-card-batches/${accessCardId}/`),
  deleteBatchById: (accessCardId) =>
    requests.delete(`/properties/access-card-batches/${accessCardId}/`),
  updateBatchById: (accessCardId, data) =>
    requests.put(`/properties/access-card-batches/${accessCardId}/`, data),

  validate: (data) =>
    requests.post(`/properties/access-cards/count/check/`, data),
};

const statistics = {
  getData: (data) => requests.post(`/admin/properties/statistics/`, data),
  sendReport: (data) =>
    requests.post(`/admin/properties/statistics/send/`, data),
  getBillingReport: (queryParams) =>
    requests.get(`/admin/billing-report/`, queryParams),
};

const exports = {
  logExport: (propertyId, data) =>
    requests.post(`/admin/properties/${propertyId}/exports/log/`, data),
};

const amenity = {
  getReservations: (queryParams) =>
    requests.get(`/amenities/reservations/`, queryParams),
  getReservationsCalendar: (queryParams) =>
    requests.get(`/amenities/reservations/calendar/`, queryParams),
  getReservation: (reservationId) =>
    requests.get(`/amenities/reservations/${reservationId}/`),
  getAmenities: (queryParams) => requests.get(`/amenities/`, queryParams),
  getAmenity: (amenityId) => requests.get(`/amenities/${amenityId}/`),
  getReservedDates: (amenityId) =>
    requests.get(`/amenities/${amenityId}/reserved-dates/`),
  getAllowedHours: (amenityId, queryParams) =>
    requests.get(`/amenities/${amenityId}/allowed-hours/`, queryParams),
  reserveAmenity: (amenityId, data) =>
    requests.post(`/amenities/${amenityId}/reservations/`, data),
  updateReservation: (reservationId, data) =>
    requests.put(`/amenities/reservations/${reservationId}/`, data),
  removeReservation: (amenityId) =>
    requests.delete(`/amenities/reservations/${amenityId}/`),
  createAmenity: (data) => requests.post(`/amenities/`, data),
  deleteAmenityImage: (amenityId, fileId) =>
    requests.delete(`/amenities/${amenityId}/image/${fileId}/`),
  updateAmenity: (amenityId, data) =>
    requests.put(`/amenities/${amenityId}/`, data),
  removeAmenity: (amenityId) => requests.delete(`/amenities/${amenityId}/`),
  updateAmenityStatus: (amenityId, data) =>
    requests.patch(`/amenities/${amenityId}/`, data),
  setAmenityStatus: (amenityId, data) =>
    requests.put(`amenities/reservations/${amenityId}/change-status/`, data),
};

const leasing = {
  getLeasingInquiries: (propertyId, queryParams) =>
    requests.get(
      `/admin/properties/${propertyId}/leasing-inquiries/`,
      queryParams,
    ),
};

const propertyLogo = {
  getBuildingsWithLogos: (queryParams) =>
    requests.get(`/admin/properties/buildings/list-with-logos/`, queryParams),
  toggleLogoDisabled: (propertyId, data) =>
    requests.post(
      `/admin/properties/${propertyId}/toggle-logo-disabled/`,
      data,
    ),
  deleteLogo: (propertyId, logoId) =>
    requests.delete(`/admin/properties/${propertyId}/logo/${logoId}/`),
};

const announcements = {
  getList: (queryParams) => requests.get(`/admin/announcements/`, queryParams),
  create: (data) => requests.post(`/admin/announcements/`, data),
  update: (id, data) => requests.put(`/admin/announcements/${id}/`, data),
  delete: (id) => requests.delete(`/admin/announcements/${id}/`),
  send: (id) => requests.post(`/admin/announcements/${id}/send/`),
};

const community = {
  getList: (queryParams) => requests.get(`/communities/`, queryParams),
  getAdminList: (queryParams) =>
    requests.get(`/admin/communities/`, queryParams),
  update: (id, data) => requests.put(`/admin/communities/${id}/`, data),
  getPostsList: (queryParams) =>
    requests.get(`/admin/communities/posts/`, queryParams),
  createPost: (data) => requests.post(`/admin/communities/posts/`, data),
  getPost: (id) => requests.get(`/admin/communities/posts/${id}/`),
  updatePost: (id, data) =>
    requests.put(`/admin/communities/posts/${id}/`, data),
  removePost: (id) => requests.delete(`/communities/posts/${id}/`),
  removeComment: (postId, commentId) =>
    requests.delete(`/communities/posts/${postId}/comments/${commentId}`),
  uploadPostPhoto: (id, data) =>
    requests.post(`/communities/posts/${id}/photo/`, data),
  getPostCommentsList: (id, queryParams) =>
    requests.get(`/admin/communities/posts/${id}/comments/`, queryParams),
  createComment: (id, data) =>
    requests.post(`/admin/communities/posts/${id}/comments/`, data),
  uploadCommentPhoto: (id, data) =>
    requests.post(`/communities/posts/${id}/comments/photo/`, data),
  togglePostLike: (id) => requests.post(`/admin/communities/posts/${id}/like/`),
  updatePostStatus: (id, data) =>
    requests.put(`/admin/communities/posts/${id}/status/`, data),
  updateCommentStatus: (id, data) =>
    requests.put(`/admin/communities/comments/${id}/status/`, data),
  getMembersList: (queryParams) =>
    requests.get(`/communities/members/`, queryParams),
  createThreads: (data) => requests.post(`/communities/threads/`, data),
  getThreads: (queryParams) =>
    requests.get('/communities/threads/', queryParams),
  threadExistsCheck: (id) =>
    requests.get(`/admin/communities/posts/${id}/thread-exists-check/`),
  putNewMessage: (threadId, data) =>
    requests.put(`/communities/threads/${threadId}/`, data),
  getChatById: (id, queryParams) =>
    requests.get(`/communities/threads/${id}/`, queryParams),
  optIn: (data) => requests.post(`/communities/opt-in/`, data),
  optOut: (data) => requests.post(`/communities/opt-out/`, data),
  preuploadDocument: (data) =>
    requests.post(`/communities/documents/upload/`, data),
  banMember: (communityId, personId, data) =>
    requests.post(
      `/admin/communities/${communityId}/persons/${personId}/ban/`,
      data,
    ),
};

const remoteDoorman = {
  getCalls: (queryParams) => requests.get(`/admin/calls/`, queryParams),
  getCallById: (callId) => requests.get(`/admin/calls/${callId}/`),
  getPopUpList: (queryParams) =>
    requests.get(`/remote-doorman-popup/`, queryParams),
  getPopUpById: (id) => requests.get(`/remote-doorman-popup/${id}/`),
  createPopUp: (data) => requests.post(`/remote-doorman-popup/`, data),
  updatePopUp: (data) =>
    requests.put(`/remote-doorman-popup/${data.id}/`, data),
  deletePopUp: (id) => requests.delete(`/remote-doorman-popup/${id}/`),
  uploadPopUpPhoto: (id) => requests.post(`/remote-doorman-popup/${id}/photo/`),
  deletePopUpPhoto: (popUpId, photoId) =>
    requests.delete(`/remote-doorman-popup/${popUpId}/photo/${photoId}/`),
  updateDoors: (buildingId, data) =>
    requests.put(
      `/admin/properties/buildings/${buildingId}/remote-doorman-doors/`,
      data,
    ),
};

const twilio = {
  getToken: () => requests.get(`/admin/frontdesk/twilio-calls/token/`),
  verifyPhoneNumber: (buildingId, data) =>
    requests.put(
      `/admin/properties/buildings/${buildingId}/front-desk-phone-number-verification/`,
      data,
    ),
};

const staffLog = {
  createPost: (buildingId, data) =>
    requests.post(`/properties/buildings/${buildingId}/staff-log-posts/`, data),
  createComment: (buildingId, postId, data) =>
    requests.post(
      `/properties/buildings/${buildingId}/staff-log-posts/${postId}/comments/`,
      data,
    ),
  getPostList: (buildingId, queryParams) =>
    requests.get(
      `/properties/buildings/${buildingId}/staff-log-posts/`,
      queryParams,
    ),
  getCommentsList: (buildingId, postId, queryParams) =>
    requests.get(
      `/properties/buildings/${buildingId}/staff-log-posts/${postId}/comments/`,
      queryParams,
    ),
  removePost: (buildingId, postId) =>
    requests.delete(
      `/properties/buildings/${buildingId}/staff-log-posts/${postId}/`,
    ),
  removeComment: (buildingId, postId, commentId) =>
    requests.delete(
      `/properties/buildings/${buildingId}/staff-log-posts/${postId}/comments/${commentId}/`,
    ),
};

const events = {
  getEventsList: (queryParams) => requests.get('/social-events/', queryParams),
  getEventData: (id) => requests.get(`/social-events/${id}`),
  createEvent: (data) => requests.post('/social-events/', data),
  updateEvent: (id, data) => requests.put(`/social-events/${id}/`, data),
  deleteEvent: (id) => requests.delete(`/social-events/${id}`),
  getEventRsvps: (queryParams) =>
    requests.get('/social-events-rsvps/', queryParams),
  deleteRsvpGuestFromEvent: (rsvpId, guestId) =>
    requests.delete(`/social-events-rsvps/${rsvpId}/guests/${guestId}/`),
  deleteRsvpFromEvent: (id) => requests.delete(`/social-events-rsvps/${id}/`),
  createRsvp: (data) => requests.post('/social-events-rsvps/', data),
  addRsvpGuest: (rsvpId, data) =>
    requests.post(`/social-events-rsvps/${rsvpId}/guests/ `, data),
};

const homeFeed = {
  getCardsList: (buildingId, queryParams) =>
    requests.get(
      `/flow-beta/buildings/${buildingId}/home-feed-cards/`,
      queryParams,
    ),
  // TODO: doesn't exist on API; should be added later
  getCardById: (buildingId, cardId) =>
    requests.get(
      `/flow-beta/buildings/${buildingId}/home-feed-cards/${cardId}/`,
    ),
  createCard: (buildingId, data) =>
    requests.post(`/flow-beta/buildings/${buildingId}/home-feed-cards/`, data),
  updateCard: (buildingId, cardId, data) =>
    requests.put(
      `/flow-beta/buildings/${buildingId}/home-feed-cards/${cardId}/`,
      data,
    ),
  deleteCard: (buildingId, cardId) =>
    requests.delete(
      `/flow-beta/buildings/${buildingId}/home-feed-cards/${cardId}/`,
    ),
  deleteCardImage: (buildingId, cardId) =>
    requests.delete(
      `/flow-beta/buildings/${buildingId}/home-feed-cards/${cardId}/photo/`,
    ),
};

const buildingGuide = {
  getCardsList: (buildingId, queryParams) =>
    requests.get(
      `/flow-beta/buildings/${buildingId}/building-guide-cards/`,
      queryParams,
    ),
  // TODO: doesn't exist on API; should be added later
  getCardById: (buildingId, cardId) =>
    requests.get(
      `/flow-beta/buildings/${buildingId}/building-guide-cards/${cardId}/`,
    ),
  createCard: (buildingId, data) =>
    requests.post(
      `/flow-beta/buildings/${buildingId}/building-guide-cards/`,
      data,
    ),
  updateCard: (buildingId, cardId, data) =>
    requests.put(
      `/flow-beta/buildings/${buildingId}/building-guide-cards/${cardId}/`,
      data,
    ),
  deleteCard: (buildingId, cardId) =>
    requests.delete(
      `/flow-beta/buildings/${buildingId}/building-guide-cards/${cardId}/`,
    ),
  deleteCardImage: (buildingId, cardId) =>
    requests.delete(
      `/flow-beta/buildings/${buildingId}/building-guide-cards/${cardId}/photo/`,
    ),
};

const yourWellbeing = {
  getCardsList: (buildingId) =>
    requests.get(`/flow-beta/buildings/${buildingId}/your-wellbeing-cards/`),
  // TODO: doesn't exist on API; should be added later
  getCardById: (buildingId, cardId) =>
    requests.get(
      `/flow-beta/buildings/${buildingId}/your-wellbeing-cards/${cardId}/`,
    ),
  createCard: (buildingId, data) =>
    requests.post(
      `/flow-beta/buildings/${buildingId}/your-wellbeing-cards/`,
      data,
    ),
  updateCard: (buildingId, cardId, data) =>
    requests.put(
      `/flow-beta/buildings/${buildingId}/your-wellbeing-cards/${cardId}/`,
      data,
    ),
  deleteCard: (buildingId, cardId) =>
    requests.delete(
      `/flow-beta/buildings/${buildingId}/your-wellbeing-cards/${cardId}/`,
    ),
  deleteCardImage: (buildingId, cardId) =>
    requests.delete(
      `/flow-beta/buildings/${buildingId}/your-wellbeing-cards/${cardId}/photo/`,
    ),
};

export default {
  accessPoint,
  admin,
  auth,
  aux,
  building,
  buildingSettings,
  buildingOperations,
  sync,
  accessPoints,
  callCenter,
  contacts,
  dashboard,
  deliveries,
  events,
  image,
  person,
  pmc,
  resident,
  serviceRequest,
  staff,
  unit,
  visitors,
  ip,
  visitorKeysData,
  messages,
  markAsViewed,
  notifications,
  notificationPreferences,
  documentLibrary,
  addresses,
  carsonActivity,
  systemExternalAccounts,
  buildingExternalAccounts,
  externalAccount,
  paymentsAccounts,
  accessControlAccounts,
  camerasAccounts,
  accountingAccounts,
  registeredUsersCount,
  deliveryTypes,
  accessCard,
  statistics,
  exports,
  amenity,
  leasing,
  propertyLogo,
  announcements,
  community,
  twilio,
  customIntegrations,
  remoteDoorman,
  staffLog,
  homeFeed,
  buildingGuide,
  yourWellbeing,
  setToken: (_token) => {
    token = _token;
  },
  setSessionToken: (newToken) => {
    sessionToken = newToken;
  },
};
